import isEmpty from "lodash/isEmpty";

import type { StripeExpressCheckoutElement } from "@stripe/stripe-js";
import { useCallback, useEffect, useMemo, useRef } from "react";
import useCallbackRef from "@shared/hooks/useCallbackRef/useCallbackRef";
import { convertPriceStringToInteger } from "@shared/modules/financials/utils/price-utils";
import { PAYMENT_MANAGER_CONFIRMATION_STRATEGY } from "@shared/modules/payments/utils/manager/utils";

import { getCountryByName } from "@shared/lib/countries";
import useDjangoWaffle from "@shared/hooks/djangoWaffle/useDjangoWaffle";
import { useStore } from "@shared/store/useStore";
import {
  NO_WALLET_METHODS,
  WalletElementSharedProps,
} from "../../components/WalletElement/WalletElement.props";
// eslint-disable-next-line import/no-cycle
import { usePaymentManager } from "../../contexts/PaymentManagerContext";
// eslint-disable-next-line import/no-cycle
import StripePaymentGateway from "../../gateways/StripePaymentGateway/StripePaymentGateway";
import { PurchaseOptsType } from "../../types";
import StripeElement, {
  StripeElementProps,
} from "../StripeElement/StripeElement.web";
import { StripeWalletPaymentError } from "../../utils/stripe/errors/StripeWalletPaymentError";
import { createPaymentLogger } from "../../utils/payment-logger";

const log = createPaymentLogger("StripeWalletElement");

/**
 * A containing element for Stripe.JS's Express Checkout element which displays virtual wallet payment options,
 * like Apple Pay, Google Pay, Paypal, Link, etc.
 * Wallet payment options are personal, and can be assumed that there will be no actors on behalf of another purchaser (ie. Box office)
 */
function StripeWalletElement({
  onElementReady,
  onElementError,
  onComponentUnmount,
  onPaymentSuccess,
  onWalletClick,
  onWalletCancel,
  onAvailablePaymentMethods,
  allowedShippingCountries,
}: StripeWalletElementProps) {
  const { manager } = usePaymentManager();
  const { isFlagActive } = useDjangoWaffle();

  // When active, the user must accept terms via the checkbox before the wallet
  // button unblocks. This means we skip auto-accepting terms on click and skip
  // resetting them on cancel, since the user's explicit acceptance should persist.
  const forceTermsForWallet = isFlagActive(
    "force_terms_acceptance_for_wallet_payments",
    manager.basket?.paymentVenue
  );

  const onElementReadyRef = useCallbackRef(onElementReady);
  const onElementErrorRef = useCallbackRef(onElementError);
  const onPaymentSuccessRef = useCallbackRef(onPaymentSuccess);
  const onWalletClickRef = useCallbackRef(onWalletClick);
  const onWalletCancelRef = useCallbackRef(onWalletCancel);
  const onAvailablePaymentMethodsRef = useCallbackRef(
    onAvailablePaymentMethods
  );
  const onComponentUnmountRef = useCallbackRef(onComponentUnmount);

  const walletElementRef = useRef<StripeExpressCheckoutElement>();
  const walletElementClickHandlerTasks = useRef<Promise<unknown>>();

  // Ref keeps the latest shipping config accessible to the click handler,
  // which is registered once on element creation and can't re-capture closures.
  const shippingConfigRef = useRef({
    required: false,
    cost: "0",
  });
  shippingConfigRef.current = {
    required: Boolean(manager.basket?.hasShippingAddressCollection),
    cost: manager.basket?.shippingCost ?? "0",
  };

  /**
   * Asynchronously prepares the basket for a wallet payment
   */
  const prepareForWalletPayment = useCallback(
    async (isStripeWalletPayment: boolean) => {
      await manager.basketPaymentService.save();
      await manager.basketPaymentService.updateIsStripeWalletPayment(
        isStripeWalletPayment
      );
      await manager.basketPaymentService.extendBasket(isStripeWalletPayment);
      if (!forceTermsForWallet) {
        await manager.basketPaymentService.acceptTerms(isStripeWalletPayment);
      }
    },
    [
      manager.basketPaymentService.save,
      manager.basketPaymentService.updateIsStripeWalletPayment,
      manager.basketPaymentService.extendBasket,
      manager.basketPaymentService.acceptTerms,
      forceTermsForWallet,
    ]
  );

  const handleElementCreated = useCallback(
    async (
      _element: Parameters<Required<StripeElementProps>["onElementCreate"]>[0]
    ) => {
      const element = _element as StripeExpressCheckoutElement;
      walletElementRef.current = element;

      /**
       * Call onElementReady when element is ready
       */
      element.on("ready", (e) => {
        log.info("Wallet element ready", {
          availablePaymentMethods: e.availablePaymentMethods
            ? Object.keys(e.availablePaymentMethods)
            : null,
        });
        // On Stripe Elements, the element may be ready to be displayed but may not actually have any payment methods available.
        // Instead of solely relying on a ready event, we also look at the available payment methods to determine readiness
        if (e.availablePaymentMethods) {
          onElementReadyRef.current?.();
          onAvailablePaymentMethodsRef.current?.({
            applePay: Boolean(e.availablePaymentMethods.applePay),
            googlePay: Boolean(e.availablePaymentMethods.googlePay),
          });
        } else {
          onAvailablePaymentMethodsRef.current?.(NO_WALLET_METHODS);
          onElementErrorRef.current?.();
        }
      });

      /**
       * Call onElementError when element has a loading error
       */
      element.once("loaderror", (error) => {
        log.error(`Wallet element load error: ${error.error}`);
        onAvailablePaymentMethodsRef.current?.(NO_WALLET_METHODS);
        onElementErrorRef.current?.();
      });

      /**
       * Handle a click event which fires when a user taps/clicks on the button.
       * The element works by firing a "click" event when a user interacts with the button,
       * which we need to respond by calling a `resolve` function within <=1s of the click event being fired.
       * This means that any asynchronous, "pre-purchase" logic that needs to happen must be done BEFORE a user is
       * able to click the Express Checkout button.
       *
       * This is the same story even with the old PaymentRequest button.
       */
      element.on("click", async (event) => {
        log.info("Wallet element clicked");

        const { required, cost } = shippingConfigRef.current;
        event.resolve(
          required
            ? {
                shippingRates: [
                  {
                    id: "1",
                    amount: convertPriceStringToInteger(cost),
                    displayName: `Standard shipping ${
                      convertPriceStringToInteger(cost) > 0 ? "(included)" : ""
                    }`,
                  },
                ],
              }
            : undefined
        );

        onWalletClickRef.current?.();

        walletElementClickHandlerTasks.current = prepareForWalletPayment(true);
        await walletElementClickHandlerTasks.current;
        return walletElementClickHandlerTasks.current;
      });

      /**
       * Handle a cancel event which fires when a user dismisses the payment interface
       */
      element.on("cancel", async () => {
        log.info("Wallet element cancelled");

        try {
          await walletElementClickHandlerTasks.current;
          await prepareForWalletPayment(false);
        } finally {
          onWalletCancelRef.current?.();
        }
      });

      /**
       * Handle a confirm event which fires when customer finalizes their payment
       */
      element.on("confirm", async (event) => {
        log.info("Wallet element confirmed");

        manager.onProcessing?.(true);
        await walletElementClickHandlerTasks.current;

        await manager.basketPaymentService.refresh();

        const purchaseOpts: PurchaseOptsType = {};

        // Hydrate purchaser info with precedence: basket > user > stripe wallet.
        // Basket fields are the source of truth when already populated
        // The logged-in user profile fills gaps next,
        // and Stripe wallet data is the final fallback.
        const basketEmail = manager.basket?.email;
        const basketName = manager.basket?.nameOnTicket;
        const basketPhone = manager.basket?.phoneNumber;
        const { user } = useStore.getState();

        const resolvedEmail =
          basketEmail || user?.email || event.billingDetails?.email || "";
        const resolvedName =
          basketName ||
          [user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
          event.billingDetails?.name ||
          "";
        const resolvedPhone =
          basketPhone ||
          user?.phone_number ||
          event.billingDetails?.phone ||
          "";

        log.info("Updating purchaser info");
        await manager.basketPaymentService.updatePurchaserInfo({
          name_on_ticket: resolvedName,
          email: resolvedEmail,
          phone_number: resolvedPhone,
        });

        if (event.billingDetails && !isEmpty(event.billingDetails.address)) {
          log.info("Updating billing address");

          purchaseOpts.billing_address = {
            street_address: event.billingDetails.address.line1,
            unit_number: event.billingDetails.address.line2 || "",
            city: event.billingDetails.address.city,
            country: event.billingDetails.address.country,
            postal: event.billingDetails.address.postal_code,
            province: event.billingDetails.address.state,
          };
        }

        // Clear any existing shipping address — the wallet confirmation SDK
        // (Apple Pay / Google Pay) captures shipping addresses itself.
        purchaseOpts.shipping_address = null;

        // Update the shipping address with the latest data, if needed
        if (event.shippingAddress && !isEmpty(event.shippingAddress.address)) {
          log.info("Updating shipping address");

          const shippingAddress = {
            street_address: event.shippingAddress.address.line1,
            unit_number: event.shippingAddress.address.line2 || "",
            city: event.shippingAddress.address.city,
            country: event.shippingAddress.address.country,
            postal: event.shippingAddress.address.postal_code,
            province: event.shippingAddress.address.state,
          };

          purchaseOpts.shipping_address = shippingAddress;
        }

        try {
          const purchaseArtifacts = await manager.purchase(
            PAYMENT_MANAGER_CONFIRMATION_STRATEGY.WALLET,
            purchaseOpts
          );
          onPaymentSuccessRef.current?.(purchaseArtifacts);
        } catch (err) {
          // Specifically check for StripeWalletPaymentError. This happens in the StripePaymentGateway under confirmWalletPayment().
          if (err instanceof StripeWalletPaymentError) {
            event.paymentFailed({
              reason: "fail",
            });
          }

          manager.onProcessing?.(false);
        }
      });
    },
    [
      manager.handler,
      manager.basketPaymentService,
      manager.purchase,
      prepareForWalletPayment,
    ]
  );

  /**
   * Call a callback function when the element unmounts.
   * The underlying StripeElement calls `.destroy()` for expressCheckout
   * elements, so listeners are already removed — we just clear the ref.
   */
  useEffect(() => {
    return () => {
      log.info("Destroying wallet element");
      walletElementRef.current?.off("ready");
      walletElementRef.current?.off("loaderror");
      walletElementRef.current?.off("click");
      walletElementRef.current?.off("cancel");
      walletElementRef.current?.off("confirm");

      onComponentUnmountRef.current?.();
    };
  }, []);

  const shippingCountries = useMemo(
    () =>
      allowedShippingCountries && allowedShippingCountries.length > 0
        ? allowedShippingCountries
            .map((country) => {
              const countryData = getCountryByName(country);
              return countryData?.countryShortCode;
            })
            .filter((country): country is string => country != null)
        : undefined,
    [allowedShippingCountries]
  );

  const shippingRequired = Boolean(
    manager.basket?.hasShippingAddressCollection
  );

  const expressCheckoutProps = useMemo(
    () => ({
      paymentMethods: {
        amazonPay: "never" as const,
        applePay: "always" as const,
        googlePay: "always" as const,
        link: "auto" as const,
        paypal: "never" as const,
      },
      buttonHeight: 40,
      buttonType: {
        applePay: "plain" as const,
        googlePay: "plain" as const,
      },
      emailRequired: true,
      phoneNumberRequired: true,
      shippingAddressRequired: shippingRequired,
      allowedShippingCountries: shippingCountries,
    }),
    [shippingRequired, shippingCountries]
  );

  // Stripe's element.update() rejects creation-only fields with console warnings.
  // Strip them out so only updatable fields (e.g. shippingAddressRequired,
  // allowedShippingCountries) are passed through to update().
  const expressCheckoutUpdateProps = useMemo(() => {
    const {
      paymentMethods: _pm,
      buttonHeight: _bh,
      buttonType: _bt,
      ...updatableProps
    } = expressCheckoutProps;
    return updatableProps;
  }, [expressCheckoutProps]);

  if (!(manager.handler?.gateway instanceof StripePaymentGateway)) {
    log.info("No Stripe payment gateway found");
    return null;
  }

  return (
    <StripeElement
      element="expressCheckout"
      alwaysCreateFresh
      cleanupStrategy="destroy"
      elementProps={expressCheckoutProps}
      elementUpdateProps={expressCheckoutUpdateProps}
      onElementCreate={handleElementCreated}
    />
  );
}

export default StripeWalletElement;

type StripeWalletElementProps = WalletElementSharedProps;
